// components/Navigation/Navbar.tsx
"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Divider,
  MenuItem,
  Paper,
  Popper,
  Grow,
  ClickAwayListener,
  MenuList,
} from "@mui/material";
import {
  Menu as MenuIcon,
  KeyboardArrowDown,
  Close,
} from "@mui/icons-material";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";

const navigationItems = [
  {
    label: "Home",
    href: "/",
    type: "link",
  },
  {
    label: "Services",
    href: "/services",
    type: "dropdown",
    items: [
      { label: "Web Development", href: "/services/web-development" },
      { label: "Automation & AI", href: "/services/automation" },
      { label: "IT & Automation Audit", href: "/services/automation-audit" },
    ],
  },
  {
    label: "Industries",
    href: "/industries",
    type: "dropdown",
    items: [
      { label: "SaaS & Platforms", href: "/industries/saas-platforms" },
      { label: "E-commerce & Retail", href: "/industries/ecommerce" },
      { label: "FinTech & Payments", href: "/industries/fintech" },
      { label: "SMEs & Startups", href: "/industries/smes-startups" },
      { label: "Agencies / White-label", href: "/industries/agencies" },
    ],
  },
  {
    label: "Portfolio",
    href: "/portfolio",
    type: "link",
  },
  {
    label: "Resources",
    href: "/resources",
    type: "dropdown",
    items: [
      { label: "Blog", href: "/resources/blog" },
      { label: "Whitepapers & Guides", href: "/resources/whitepapers" },
      { label: "Tools & Templates", href: "/resources/tools" },
      // { label: "FAQ", href: "/resources/faq" },
    ],
  },
  {
    label: "About Us",
    href: "/about",
    type: "dropdown",
    items: [
      { label: "Our Story & Vision", href: "/about" },
      // { label: "Team", href: "/about/team" },
      { label: "Careers", href: "/about/careers" },
      { label: "Partners", href: "/about/partners" },
    ],
  },
];

const Navbar = () => {
  const theme = useTheme();
  const pathname = usePathname();
  const [mobileDrawer, setMobileDrawer] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<{
    [key: string]: boolean;
  }>({});

  const isMobile = useMediaQuery(theme.breakpoints.down("md"), {
    noSsr: true,
    defaultMatches: false,
  });

  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  const anchorRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const handleConsult = () => {
    window.open("https://calendly.com/linxarlabs-info", "_blank");
  };

  const handleToggle = (label: string) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const handleClose = (event: Event | React.SyntheticEvent, label: string) => {
    if (
      anchorRefs.current[label] &&
      anchorRefs.current[label]!.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenDropdowns((prev) => ({
      ...prev,
      [label]: false,
    }));
  };

  const handleListKeyDown = (event: React.KeyboardEvent, label: string) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenDropdowns((prev) => ({
        ...prev,
        [label]: false,
      }));
    } else if (event.key === "Escape") {
      setOpenDropdowns((prev) => ({
        ...prev,
        [label]: false,
      }));
    }
  };

  const isActiveLink = (href: string) => {
    if (href === "/") return pathname === "/";
    if (href === "/about")
      return pathname === "/about" || pathname.startsWith("/about/");
    if (href === "/services") return pathname.startsWith("/services");
    if (href === "/industries") return pathname.startsWith("/industries");
    if (href === "/resources") return pathname.startsWith("/resources");
    return pathname === href || pathname.startsWith(href + "/");
  };

  const renderDesktopNav = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
      {navigationItems.map((item) => (
        <Box key={item.label}>
          {item.type === "link" ? (
            <Button
              component={Link}
              href={item.href}
              sx={{
                color: isActiveLink(item.href)
                  ? theme.palette.primary.main
                  : "text.primary",
                fontWeight: isActiveLink(item.href) ? 600 : 400,
                position: "relative",
                "&:after": {
                  content: '""',
                  position: "absolute",
                  bottom: -4,
                  left: 0,
                  width: isActiveLink(item.href) ? "100%" : "0%",
                  height: 2,
                  backgroundColor: theme.palette.primary.main,
                  transition: "width 0.3s ease",
                },
                "&:hover:after": {
                  width: "100%",
                },
              }}
            >
              {item.label}
            </Button>
          ) : (
            <Box>
              <Button
                ref={(el: HTMLButtonElement | null) => {
                  anchorRefs.current[item.label] = el;
                }}
                id={`${item.label}-button`}
                aria-controls={
                  openDropdowns[item.label] ? `${item.label}-menu` : undefined
                }
                aria-expanded={openDropdowns[item.label] ? "true" : undefined}
                aria-haspopup="true"
                onClick={() => handleToggle(item.label)}
                endIcon={<KeyboardArrowDown />}
                sx={{
                  color: isActiveLink(item.href)
                    ? theme.palette.primary.main
                    : "text.primary",
                  fontWeight: isActiveLink(item.href) ? 600 : 400,
                }}
              >
                {item.label}
              </Button>

              <Popper
                open={openDropdowns[item.label] || false}
                anchorEl={anchorRefs.current[item.label]}
                role={undefined}
                placement="bottom-start"
                transition
                disablePortal
                keepMounted
                sx={{
                  pt: 2,
                }}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === "bottom-start"
                          ? "left top"
                          : "left bottom",
                    }}
                  >
                    <Paper>
                      <ClickAwayListener
                        onClickAway={(event) => handleClose(event, item.label)}
                      >
                        <MenuList
                          autoFocusItem={openDropdowns[item.label]}
                          id={`${item.label}-menu`}
                          aria-labelledby={`${item.label}-button`}
                          onKeyDown={(event) =>
                            handleListKeyDown(event, item.label)
                          }
                          sx={{ minWidth: 200 }}
                        >
                          {item.items?.map((subItem) => (
                            <MenuItem
                              key={subItem.label}
                              onClick={(event) =>
                                handleClose(event, item.label)
                              }
                              component={Link}
                              href={subItem.href}
                              sx={{
                                color: isActiveLink(subItem.href)
                                  ? theme.palette.primary.main
                                  : "text.primary",
                                fontWeight: isActiveLink(subItem.href)
                                  ? 600
                                  : 400,
                              }}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  // Mobile Navigation
  const MobileNav = () => (
    <List sx={{ width: 280, pt: 2 }}>
      {navigationItems.map((item) => (
        <Box key={item.label}>
          {item.type === "link" ? (
            <ListItem
              component={Link}
              href={item.href}
              onClick={() => setMobileDrawer(false)}
              sx={{
                color: isActiveLink(item.href)
                  ? theme.palette.primary.main
                  : "text.primary",
                fontWeight: isActiveLink(item.href) ? 600 : 400,
              }}
            >
              <ListItemText primary={item.label} />
            </ListItem>
          ) : (
            <>
              <ListItem
                onClick={() => handleToggle(item.label)}
                sx={{
                  cursor: "pointer",
                  color: isActiveLink(item.href)
                    ? theme.palette.primary.main
                    : "text.primary",
                  fontWeight: isActiveLink(item.href) ? 600 : 400,
                }}
              >
                <ListItemText primary={item.label} />
                <KeyboardArrowDown />
              </ListItem>
              {openDropdowns[item.label] && (
                <Box sx={{ pl: 4 }}>
                  {item.items?.map((subItem) => (
                    <ListItem
                      key={subItem.label}
                      component={Link}
                      href={subItem.href}
                      onClick={() => {
                        setOpenDropdowns((prev) => ({
                          ...prev,
                          [item.label]: false,
                        }));
                        setMobileDrawer(false);
                      }}
                      sx={{
                        color: isActiveLink(subItem.href)
                          ? theme.palette.primary.main
                          : "text.primary",
                        fontWeight: isActiveLink(subItem.href) ? 600 : 400,
                        py: 1,
                      }}
                    >
                      <ListItemText primary={subItem.label} />
                    </ListItem>
                  ))}
                </Box>
              )}
            </>
          )}
          <Divider />
        </Box>
      ))}
    </List>
  );

  // if (!mounted) {
  return hydrated ? (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: "blur(10px)",
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Link
              href="/"
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Image
                src="/linxar-main-logo.svg"
                alt="LinXar Labs"
                width={150}
                height={50}
                style={{
                  width: "auto",
                  height: "50px",
                  maxWidth: "150px",
                }}
              />
            </Link>
          </Box>

          {/* Desktop Navigation - INLINE RENDER */}
          {!isMobile && renderDesktopNav()}

          {/* CTA Button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {!isMobile && (
              <Button
                variant="contained"
                onClick={handleConsult}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Book a Free Call
              </Button>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileDrawer(true)}
                sx={{
                  color: "text.primary",
                }}
                aria-label="Open Menu"
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>

          {/* Mobile Drawer */}
          <Drawer
            anchor="right"
            open={mobileDrawer}
            onClose={() => {
              setMobileDrawer(false);
              setOpenDropdowns({});
            }}
            sx={{
              "& .MuiDrawer-paper": {
                width: 300,
                boxSizing: "border-box",
              },
            }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Image
                src="/linxar-main-logo.svg"
                alt="LinXar Labs"
                width={150}
                height={50}
                style={{
                  width: "auto",
                  height: "50px",
                  maxWidth: "150px",
                }}
              />
              <IconButton onClick={() => setMobileDrawer(false)}>
                <Close />
              </IconButton>
            </Box>
            <Divider />
            <MobileNav />
            <Box sx={{ p: 2 }}>
              <Button
                variant="contained"
                fullWidth
                component={Link}
                href="/contact"
                onClick={() => setMobileDrawer(false)}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  mt: 2,
                }}
              >
                Book a Free Call
              </Button>
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  ) : null;
};
// };
export default Navbar;
